<template>
    <div class="card" style="width:800px;max-width:100%;">
        <div class="card-modal" style="overflow:auto;max-height:700px;">
            <div v-if="loading">
                <b-loading :is-full-page="true" v-model="loading" :can-cancel="false"></b-loading>
            </div>
            <header class="modal-card-head">
                <p class="modal-card-title">Confirm Trip Changes</p>
                <button type="button" class="delete" @click="$emit('close')" />
            </header>
            <section class="modal-card-body">
                <div v-if="!success">

                    <b-table :data="sortedSummary" ref="table" class="mb-3" :narrowed="true" :mobile-cards="true"
                        :scrollable="true">
                        <b-table-column v-slot="props">
                            <div class="d-block">
                                <p class="mb-0 has-text-weight-medium">{{ props.row.title }}</p>
                                <p class="is-size-7 mb-0">{{ props.row.dates.startDate | formatDate2 }} - {{
                                    props.row.dates.endDate | formatDate2 }}</p>
                            </div>
                        </b-table-column>
                        <b-table-column v-slot="props">
                            {{ props.row.provider }}
                        </b-table-column>
                        <b-table-column field="requestStatus" v-slot="props">
                            <span :class="[
                                'tag',
                                { 'is-danger': props.row.requestStatus == 'Cancel' }
                            ]">
                                {{ props.row.requestStatus }}
                            </span>
                        </b-table-column>
                    </b-table>
                    <p class="has-text-centered is-size-6 my-4">{{ cancelMessage }} This
                        action cannot be undone.</p>
                </div>
            </section>
            <footer class="modal-card-foot">
                <b-button class="cancel-button" @click="close()">Do Not Cancel</b-button>
                <b-button v-if="!success" @click="submit()" class="confirm-button button is-danger is-wide">
                    {{ submitButtonText }}
                </b-button>
            </footer>
        </div>
    </div>
</template>

<script>

export default {
    name: "CancelConfirmModal",
    props: {
        cancelTripPayload: {
            required: true,
            type: Object,
        },
        activeTripSegments: {
            required: true,
            type: Array,
        },
        tstInfo: {
            required: false,
            default: null
        },
    },
    data() {
        return {
            loading: false,
            success: false,
        };
    },
    methods: {
        close() {
            this.$emit('close');
        },
        failure(msg) {
            this.$buefy.toast.open({
                duration: 5000,
                message: msg,
                position: "is-bottom",
                type: "is-danger",
            });
        },
        submit() {
            var body = {...this.cancelTripPayload, summary: this.summary };

            this.loading = true;

            this.$app
                .huddleLabsServerCall("amadeus/trips/unified-cancel", body)
                .then((result) => {
                    this.result = result
                })
                .catch((error) => {
                    console.error("API error:", error);
                    this.failure("There was an error communicating with the server");
                })
                .finally(() => {
                    this.loading = false;
                    this.$emit('complete', this.result);
                    this.$emit('close');
                });
        },
        buildSummary() {
            const segments = [...this.hotelSegments, ...this.carSegments];

            // Process hotel and car segments
            const summarizedSegments = segments.map(segment => this.summarizeSegment(segment));

            // Process air segments separately
            if (this.airTsts) {
                const airSummarized = this.airTsts.map(tst => this.summarizeAirSegment(tst));
                return [...summarizedSegments, ...airSummarized];
            }

            return summarizedSegments;
        },

        summarizeSegment(segment) {
            const isForCancellation = this.isSegmentForCancellation(segment);
            return {
                title: this.$helpers.segmentTitle(segment),
                dates: this.getSegmentDates(segment),
                provider: this.getSegmentProvider(segment),
                requestStatus: isForCancellation ? "Cancel" : "Unchanged",
                type: segment.type,
                tattoo: segment.tattoo
            };
        },

        summarizeAirSegment(tst) {
            const isForCancellation = this.isTstForCancellation(tst);
            const firstSeg = tst.segments[0];
            const lastSeg = tst.segments[tst.segments.length - 1];

            return {
                title: this.$helpers.tstTitle(tst),
                dates: {
                    startDate: firstSeg.startDate.date,
                    endDate: lastSeg.endDate.date
                },
                provider: this.$helpers.airlineName(tst.airlineCode) || "",
                requestStatus: isForCancellation ? "Cancel" : "Unchanged",
                type: "air",
                tattoo: tst.tattoos
            };
        },
        isSegmentForCancellation(segment) {
            switch (segment.type) {
                case "hotel":
                    return this.hotelTattoos.includes(segment.tattoo);
                case "car":
                    return this.carTattoos.includes(segment.tattoo);
                default:
                    return false
            }
        },
        isTstForCancellation(tst) {
            return this.airTattoos.some(tattooGroup =>
                tattooGroup.length === tst.tattoos.length &&
                tattooGroup.every((tattoo, index) => tattoo === tst.tattoos[index])
            );
        },
        getSegmentDates(segment) {
            return {
                startDate: segment.startDate.date,
                endDate: segment.endDate.date
            };
        },
        getSegmentProvider(segment) {

            switch (segment.type) {
                case "hotel":
                    return segment.hotel.hotelName;
                case "car":
                    return segment.carProvider.name;
                default:
                    return "";
            }
        }
    },
    computed: {
        airTsts() {
            return this.tstInfo;
        },
        hotelSegments() {
            return this.activeTripSegments.filter((segment) => segment.type === "hotel");
        },
        carSegments() {
            return this.activeTripSegments.filter((segment) => segment.type === "car");
        },
        carTattoos() {
            return this.cancelTripPayload.carTattoos || [];
        },
        hotelTattoos() {
            return this.cancelTripPayload.hotelTattoos || [];
        },
        airTattoos() {
            return this.cancelTripPayload.airTattoos || [];
        },
        submitButtonText() {
            if (this.activeTripSegments.length === this.cancelTripPayload.segments.length) {
                return "Yes, Cancel This Reservation";
            } else if (this.cancelTripPayload.segments.length === 1) {
                return "Yes, Cancel This Segment";
            } else {
                return "Cancel Selected Segments";
            }
        },
        cancelMessage() {
            if (this.activeTripSegments.length === this.cancelTripPayload.segments.length) {
                return "Are you sure you wish to cancel this trip?";
            } else if (this.cancelTripPayload.segments.length === 1) {
                return "Are you sure you wish to cancel this segment?";
            } else {
                return "Are you sure you wish to cancel the selected segments?";
            }
        },
        summary() {
            return this.buildSummary();
        },
        sortedSummary() {
            // Create a copy of the summary array to avoid mutating the original
            return [...this.summary].sort((a, b) => {
                return a.type.localeCompare(b.type); // Sort by type
            });
        },

    }
};
</script>

<style scoped>
button {
    white-space: normal;
    text-align: center;
    word-wrap: break-word;
    overflow: hidden;
}

@media (max-width: 600px) {

    .cancel-button,
    .confirm-button {
        width: 100%;
        height: 3.5em;
    }

    /* Allow text to wrap into multiple lines */
    .confirm-button {
        white-space: normal;
    }
}

@media (max-width: 400px) {

    .cancel-button,
    .confirm-button {
        height: 4em;
    }
}

@media (max-width: 300px) {

    .cancel-button,
    .confirm-button {
        height: 5em;
    }
}
</style>
